/**
 * Построчное сравнение двух версий статьи.
 *
 * Алгоритм — наибольшая общая подпоследовательность строк. Наивное
 * сравнение «строка N против строки N» показывает всю статью изменённой,
 * стоит автору вставить абзац в начало: ревьюеру пришлось бы читать
 * текст целиком вместо трёх правок.
 *
 * Матрица длин — O(n·m) по памяти. Для регламентов это сотни строк,
 * то есть десятки тысяч ячеек; ограничение LIMIT ниже отсекает
 * патологический случай, когда кто-то вставит в статью выгрузку логов.
 */

const LIMIT = 3000

export function diffLines(before, after) {
  const a = splitLines(before)
  const b = splitLines(after)

  if (a.length > LIMIT || b.length > LIMIT) {
    // Слишком большие тексты: показываем целиком, без разметки правок.
    return [
      ...a.map((text) => ({ type: 'remove', text })),
      ...b.map((text) => ({ type: 'add', text })),
    ]
  }

  // lcs[i][j] — длина общей подпоследовательности для хвостов a[i:], b[j:].
  const lcs = Array.from({ length: a.length + 1 }, () =>
    new Uint32Array(b.length + 1))

  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      lcs[i][j] = a[i] === b[j]
        ? lcs[i + 1][j + 1] + 1
        : Math.max(lcs[i + 1][j], lcs[i][j + 1])
    }
  }

  const out = []
  let i = 0
  let j = 0
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      out.push({ type: 'same', text: a[i] })
      i += 1
      j += 1
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      out.push({ type: 'remove', text: a[i] })
      i += 1
    } else {
      out.push({ type: 'add', text: b[j] })
      j += 1
    }
  }
  while (i < a.length) { out.push({ type: 'remove', text: a[i] }); i += 1 }
  while (j < b.length) { out.push({ type: 'add', text: b[j] }); j += 1 }

  return out
}

/**
 * Свернуть неизменные куски, оставив контекст вокруг правок.
 *
 * Без этого diff статьи на двести строк с одной правкой — это двести
 * строк, среди которых правку надо искать глазами.
 */
export function collapseUnchanged(rows, context = 3) {
  const keep = new Set()
  rows.forEach((row, idx) => {
    if (row.type === 'same') return
    for (let k = idx - context; k <= idx + context; k += 1) {
      if (k >= 0 && k < rows.length) keep.add(k)
    }
  })

  const out = []
  let skipped = 0
  rows.forEach((row, idx) => {
    if (keep.has(idx)) {
      if (skipped) {
        out.push({ type: 'gap', count: skipped })
        skipped = 0
      }
      out.push(row)
    } else {
      skipped += 1
    }
  })
  if (skipped) out.push({ type: 'gap', count: skipped })
  return out
}

/** Сводка правок для заголовка: «+12 −3». */
export function diffStats(rows) {
  return rows.reduce(
    (acc, r) => {
      if (r.type === 'add') acc.added += 1
      if (r.type === 'remove') acc.removed += 1
      return acc
    },
    { added: 0, removed: 0 },
  )
}

function splitLines(text) {
  const s = String(text || '')
  if (!s) return []
  return s.replace(/\r\n?/g, '\n').split('\n')
}
